import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GlobalPrivilegesResource } from '../../resources/global-privileges.resource';
import { combineLatest, forkJoin, Observable, Subscription } from 'rxjs';
import {
  GroupGlobalPermissions,
  PrivilegedTenant,
  UserGlobalPermissions,
  ZpmPrivilege,
  ZpmRole,
} from '../../../shared/models/generated/ziroModels';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import {
  GroupGlobalPrivilegesFormData,
  PrivilegeRole,
  PrivilegeScope,
  PrivilegesFormData,
  PrivilegeType,
  UserGlobalPrivilegesFormData,
} from '../../microsoft/shared/privileges/privilege-models';
import { UserGroupPrivilegesModalProperties } from '../../microsoft/shared/privileges/user-group-privileges-modal/user-group-privileges-modal.component';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { delayWhen, map } from 'rxjs/operators';
import { UserGroupPrivilegesService } from '../../microsoft/shared/privileges/user-group-privileges.service';
import { ToastService } from '../../../shared/services/toast.service';
import { MicrosoftTenantsStateContext } from '../../../shared/contexts/microsoft-tenants-state.context';

interface GlobalPrivilegesDatatableRow extends DatatableRow {
  type: PrivilegeType;
  name: string;
  role: PrivilegeRole;
  privileges: ZpmPrivilege[];
}

@Component({
  selector: 'ziro-global-privileges',
  standalone: false,
  templateUrl: 'global-privileges.component.html',
  styleUrls: ['../../admin-page.scss'],
})
export class GlobalPrivilegesComponent implements OnInit, OnDestroy {
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _globalPrivilegesResource = inject(GlobalPrivilegesResource);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _bottomNavService = inject(BottomNavService);
  private _userGroupPrivilegesService = inject(UserGroupPrivilegesService);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);

  columns: DatatableColumn<GlobalPrivilegesDatatableRow>[] = [
    {
      name: 'type',
      label: 'tkey;admin.privileges.table.type',
      columnCharacterWidth: 8,
      pipe: {
        transform: (value: PrivilegeType): string => this._userGroupPrivilegesService.humanReadableType(value),
      },
    },
    {
      name: 'name',
      label: 'tkey;admin.privileges.table.name',
      columnCharacterWidth: 40,
    },
    {
      name: 'role',
      label: 'tkey;admin.privileges.table.role',
      columnCharacterWidth: 25,
      pipe: {
        transform: (value: PrivilegeRole): string => this._userGroupPrivilegesService.humanReadableRole(value),
      },
    },
  ];
  rows: GlobalPrivilegesDatatableRow[];
  typeFilterOptions: FilterSelectOption[] = [
    {
      value: PrivilegeType.USER,
      label: this._userGroupPrivilegesService.humanReadableType(PrivilegeType.USER),
    },
    {
      value: PrivilegeType.GROUP,
      label: this._userGroupPrivilegesService.humanReadableType(PrivilegeType.GROUP),
    },
  ];
  roleFilterOptions: FilterSelectOption[] = [
    {
      value: PrivilegeRole.GLOBAL_ADMINISTRATOR,
      label: this._userGroupPrivilegesService.humanReadableRole(PrivilegeRole.GLOBAL_ADMINISTRATOR),
    },
    {
      value: PrivilegeRole.TENANT_ADMINISTRATOR,
      label: this._userGroupPrivilegesService.humanReadableRole(PrivilegeRole.TENANT_ADMINISTRATOR),
    },
    {
      value: PrivilegeRole.CUSTOM,
      label: this._userGroupPrivilegesService.humanReadableRole(PrivilegeRole.CUSTOM),
    },
  ];
  privilegeFilterOptions: FilterSelectOption[] = [
    {
      value: ZpmPrivilege.TEAMS_USER_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.teams_user_management',
    },
    {
      value: ZpmPrivilege.RESOURCE_ACCOUNT_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.resource_account_management',
    },
    {
      value: ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.auto_attendant_management',
    },
    {
      value: ZpmPrivilege.CALL_QUEUE_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.call_queue_management',
    },
    {
      value: ZpmPrivilege.EMERGENCY_LOCATION_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.emergency_location_management',
    },
    {
      value: ZpmPrivilege.CONFIG_REPORTS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.configuration_reports',
    },
    {
      value: ZpmPrivilege.AUDIT_REPORTS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.audit_reports',
    },
    {
      value: ZpmPrivilege.CDR_REPORTS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.cdr_reports',
    },
    {
      value: ZpmPrivilege.MIGRATIONS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.migration',
    },
    {
      value: ZpmPrivilege.AUTOMATION,
      label: 'tkey;admin.user_group_privileges_modal.privileges.automation',
    },
  ];
  isLoading = true;

  private _allUpns: string[];
  private _allGroupNames: string[];
  private _allTenants: PrivilegedTenant[];
  private _subscription = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.privileges' }]);

    const table$ = this._initTable();
    const tenants$ = this._microsoftTenantsStateContext.fieldConfigState$.pipe(
      map((tenantsFieldConfig) => {
        this._allTenants = tenantsFieldConfig.privilegedTenants;
      }),
    );

    const sub = combineLatest([tenants$, table$]).subscribe(() => {
      this._initBottomNav();
      this.isLoading = false;
    });
    this._subscription.add(sub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _initTable(): Observable<void> {
    return forkJoin([this._globalPrivilegesResource.getUsers(), this._globalPrivilegesResource.getGroups()]).pipe(
      map(([users, groups]) => {
        this._allUpns = users.map((user) => user.upn);
        this._allGroupNames = groups.map((group) => group.displayName);
        this.rows = this._buildRows(users, groups);
      }),
    );
  }

  private _buildRows(users: UserGlobalPermissions[], groups: GroupGlobalPermissions[]): GlobalPrivilegesDatatableRow[] {
    const rows = [
      ...users.map((user) => this._mapUserToRow(user)),
      ...groups.map((group) => this._mapGroupToRow(group)),
    ];
    rows.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return rows;
  }

  private _mapUserToRow(user: UserGlobalPermissions): GlobalPrivilegesDatatableRow {
    return {
      type: PrivilegeType.USER,
      name: user.upn,
      role: this._getRole(user),
      privileges: user.privileges,
      actions: [
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DEFAULT,
          dataAutomation: 'global-privileges-copy-button',
          icon: ZiroIcons.COPY,
          onClick: () => {
            const formData = this._getUserModalFormData(user);
            formData.upn = '';
            this._openModal(this._allUpns, formData, false);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_PRIMARY,
          dataAutomation: 'global-privileges-edit-button',
          icon: ZiroIcons.EDIT,
          onClick: () => {
            const formData = this._getUserModalFormData(user);
            this._openModal(this._allUpns, formData, true);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DANGER,
          dataAutomation: 'global-privileges-delete-button',
          icon: ZiroIcons.DELETE_SMALL,
          onClick: () => {
            return this._openDeleteModal(PrivilegeType.USER, user.upn, () => {
              return this._globalPrivilegesResource.deleteUser(user.upn);
            });
          },
        },
      ],
    };
  }

  private _mapGroupToRow(group: GroupGlobalPermissions): GlobalPrivilegesDatatableRow {
    return {
      type: PrivilegeType.GROUP,
      name: group.displayName,
      role: this._getRole(group),
      privileges: group.privileges,
      actions: [
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DEFAULT,
          dataAutomation: 'global-privileges-copy-button',
          icon: ZiroIcons.COPY,
          onClick: () => {
            const formData = this._getGroupModalFormData(group);
            formData.displayName = '';
            formData.tenantGuid = '';
            this._openModal(this._allGroupNames, formData, false);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_PRIMARY,
          dataAutomation: 'global-privileges-edit-button',
          icon: ZiroIcons.EDIT,
          onClick: () => {
            const formData = this._getGroupModalFormData(group);
            this._openModal(this._allGroupNames, formData, true);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DANGER,
          dataAutomation: 'global-privileges-delete-button',
          icon: ZiroIcons.DELETE_SMALL,
          onClick: () => {
            return this._openDeleteModal(PrivilegeType.GROUP, group.displayName, () => {
              return this._globalPrivilegesResource.deleteGroup(group.groupTenantGuid, group.displayName);
            });
          },
        },
      ],
    };
  }

  private _openDeleteModal(type: PrivilegeType, name: string, cb: () => Observable<void>): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this._translateService.instant('tkey;admin.privileges.delete.modal.body', {
          type: this._translateService.instant(this._userGroupPrivilegesService.humanReadableType(type)),
          name: name,
        }),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () =>
              cb().pipe(
                delayWhen(() => this._initTable()),
                map(() => {
                  const serviceType =
                    type === PrivilegeType.USER
                      ? 'tkey;admin.user_group_privileges_modal.user.toast.title'
                      : 'tkey;admin.user_group_privileges_modal.group.toast.title';
                  this._toastService.pushDeleteToast(this._translateService.instant(serviceType), name);
                }),
              ),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _openModal(existingEntities: string[], entity: PrivilegesFormData, isExisting: boolean): void {
    const modalProperties: UserGroupPrivilegesModalProperties = {
      entity: entity,
      existingEntities: existingEntities,
      allTenants: this._allTenants,
      isExisting: isExisting,
      afterSubmitFn: () => this._initTable(),
    };
    this._userGroupPrivilegesService.openModal(modalProperties);
  }

  private _getRole(entity: UserGlobalPermissions | GroupGlobalPermissions): PrivilegeRole {
    if (entity.roles.some((role) => role === ZpmRole.GLOBAL_ADMIN)) {
      return PrivilegeRole.GLOBAL_ADMINISTRATOR;
    } else if (entity.roles.some((role) => role === ZpmRole.TENANT_ADMIN)) {
      return PrivilegeRole.TENANT_ADMINISTRATOR;
    }
    return PrivilegeRole.CUSTOM;
  }

  private _getUserModalFormData(entity: UserGlobalPermissions): UserGlobalPrivilegesFormData {
    return {
      scope: PrivilegeScope.GLOBAL,
      type: PrivilegeType.USER,
      upn: entity.upn,
      role: this._getRole(entity),
      ...this._userGroupPrivilegesService.getModalFormDataPrivileges(entity.privileges),
    };
  }

  private _getGroupModalFormData(entity: GroupGlobalPermissions): GroupGlobalPrivilegesFormData {
    return {
      scope: PrivilegeScope.GLOBAL,
      type: PrivilegeType.GROUP,
      displayName: entity.displayName,
      tenantGuid: entity.groupTenantGuid,
      role: this._getRole(entity),
      ...this._userGroupPrivilegesService.getModalFormDataPrivileges(entity.privileges),
    };
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'global-privileges-add-user-button',
          dataAutomation: 'global-privileges-add-user-button',
          type: ButtonTypes.BUTTON,
          label: 'tkey;admin.privileges.add_user',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.ADD_USER_TO_LIST,
          cb: () => {
            const newUser: UserGlobalPrivilegesFormData = {
              scope: PrivilegeScope.GLOBAL,
              type: PrivilegeType.USER,
              upn: '',
              role: null,
              ...this._userGroupPrivilegesService.defaultPrivileges(),
            };
            this._openModal(this._allUpns, newUser, false);
          },
        },
        {
          id: 'global-privileges-add-group-button',
          dataAutomation: 'global-privileges-add-group-button',
          type: ButtonTypes.BUTTON,
          label: 'tkey;admin.privileges.add_group',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.ADD_USER_GROUP_TO_LIST,
          state: {
            buttonDisableState: {
              disabled: this._allTenants.length === 0,
              tooltipKey: 'tkey;admin.privileges.add_group.disabled',
            },
            tooltipVisible: this._allTenants.length === 0,
          },
          cb: () => {
            const newGroup: GroupGlobalPrivilegesFormData = {
              scope: PrivilegeScope.GLOBAL,
              type: PrivilegeType.GROUP,
              displayName: '',
              tenantGuid: '',
              role: null,
              ...this._userGroupPrivilegesService.defaultPrivileges(),
            };
            this._openModal(this._allGroupNames, newGroup, false);
          },
        },
      ]),
    );
  }

  isTypeMatch(filters: string[], row: GlobalPrivilegesDatatableRow): boolean {
    return !filters.length || filters.includes(row.type);
  }

  isRoleMatch(filters: string[], row: GlobalPrivilegesDatatableRow): boolean {
    return !filters.length || filters.includes(row.role);
  }

  isPrivilegeMatch(filters: string[], row: GlobalPrivilegesDatatableRow): boolean {
    return (
      !filters.length ||
      row.privileges.some((privilege) => filters.includes(privilege)) ||
      row.role === PrivilegeRole.TENANT_ADMINISTRATOR ||
      row.role === PrivilegeRole.GLOBAL_ADMINISTRATOR
    );
  }
}
